import React, { useEffect, useState } from 'react';
import {
    Container, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Paper, Button, Chip, CircularProgress, Box, FormControlLabel, Switch, TextField, Tabs, Tab,
    TablePagination, IconButton, Tooltip, Dialog, DialogTitle, DialogContent, List, ListItem,
    ListItemText, Divider
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
    fetchAllUsers, fetchSystemSettings, updateUserPayment, updateSystemSetting,
    fetchUserProfiles, fetchAuspiciousRequests, toggleUserVisibility, fetchUserChats, fetchChatMessages
} from '../features/admin/adminAction';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import ChatIcon from '@mui/icons-material/Chat';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

const AdminDashboard = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const {
        users, loading, settings, settingsLoading, userProfiles, profilesLoading,
        auspiciousRequests, requestsLoading, pagination, selectedUserChats,
        selectedChatMessages, chatsLoading, messagesLoading
    } = useSelector((state) => state.admin);

    const [searchTerm, setSearchTerm] = useState('');
    const [currentTab, setCurrentTab] = useState(0);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    // Chat Modal State
    const [chatModalOpen, setChatModalOpen] = useState(false);
    const [selectedUserForChat, setSelectedUserForChat] = useState(null);
    const [selectedConversation, setSelectedConversation] = useState(null);

    const token = localStorage.getItem('userToken');

    useEffect(() => {
        if (!token) {
            navigate('/login-admin');
        } else {
            // Initial load
            handleFetchUsers();
            dispatch(fetchSystemSettings());
            dispatch(fetchUserProfiles());
            dispatch(fetchAuspiciousRequests());
        }
    }, [token, navigate, dispatch]);

    const handleFetchUsers = (query = searchTerm, newPage = page, newLimit = rowsPerPage) => {
        dispatch(fetchAllUsers({
            query,
            page: newPage + 1, // API uses 1-based indexing
            limit: newLimit,
            sort: 'desc'
        }));
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
        handleFetchUsers(searchTerm, newPage, rowsPerPage);
    };

    const handleChangeRowsPerPage = (event) => {
        const newLimit = parseInt(event.target.value, 10);
        setRowsPerPage(newLimit);
        setPage(0);
        handleFetchUsers(searchTerm, 0, newLimit);
    };

    const handleSearch = () => {
        setPage(0);
        handleFetchUsers(searchTerm, 0, rowsPerPage);
    };

    const handleUpdatePayment = (userId, isPaid) => {
        if (window.confirm(`Are you sure you want to ${isPaid ? 'activate' : 'revoke'} this user's permission?`)) {
            dispatch(updateUserPayment({ userId, isPaid }));
        }
    };

    const handleToggleVisibility = (userId, currentStatus) => {
        // currentStatus: 1 (hidden) or 0 (visible)
        // If current is hidden (1), we want to unhide (0). If visible (0), hide (1).
        const newStatus = currentStatus ? 0 : 1;
        const action = newStatus ? 'hide' : 'unhide';

        if (window.confirm(`Are you sure you want to ${action} this user?`)) {
            dispatch(toggleUserVisibility({ userId, isHidden: !!newStatus }));
        }
    };

    const handleOpenChats = (user) => {
        setSelectedUserForChat(user);
        setChatModalOpen(true);
        setSelectedConversation(null);
        dispatch(fetchUserChats(user.id));
    };

    const handleSelectConversation = (conversationId) => {
        setSelectedConversation(conversationId);
        dispatch(fetchChatMessages(conversationId));
    };

    const handleCloseChatModal = () => {
        setChatModalOpen(false);
        setSelectedUserForChat(null);
        setSelectedConversation(null);
    };

    const handleToggleEmailVerification = (checked) => {
        dispatch(updateSystemSetting({
            key: 'email_verification_enabled',
            value: checked ? 'true' : 'false'
        }));
    };

    const handleTabChange = (event, newValue) => {
        setCurrentTab(newValue);
    };

    if (loading && !users?.length) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 5 }}><CircularProgress /></Box>;

    return (
        <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
            <Typography variant="h4" gutterBottom component="div" sx={{ color: '#71123e', fontWeight: 'bold' }}>
                Admin Dashboard
            </Typography>

            {/* SYSTEM SETTINGS SECTION */}
            <Paper sx={{ p: 3, mb: 4, border: '1px solid #e0e0e0' }}>
                <Typography variant="h6" gutterBottom sx={{ color: '#71123e', fontWeight: 'bold' }}>
                    System Settings
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={settings.email_verification_enabled === 'true'}
                                onChange={(e) => handleToggleEmailVerification(e.target.checked)}
                                color="primary"
                            />
                        }
                        label="Enable Email Verification for New Users"
                    />
                </Box>
            </Paper>

            {/* TABS */}
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
                <Tabs value={currentTab} onChange={handleTabChange} aria-label="admin tabs">
                    <Tab label="User Payments & Management" />
                    <Tab label="User Profiles" />
                    <Tab label="Auspicious Requests" />
                </Tabs>
            </Box>

            {/* TAB 1: USER PAYMENTS & MANAGEMENT */}
            {currentTab === 0 && (
                <>
                    <Box sx={{ mb: 2, display: 'flex', gap: 2 }}>
                        <TextField
                            label="Search by Name or Email"
                            variant="outlined"
                            size="small"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    handleSearch();
                                }
                            }}
                            fullWidth
                            sx={{ backgroundColor: 'white' }}
                        />
                        <Button
                            variant="contained"
                            onClick={handleSearch}
                            sx={{ backgroundColor: '#71123e', '&:hover': { backgroundColor: '#5a0e31' } }}
                        >
                            Search
                        </Button>
                    </Box>
                    <TableContainer component={Paper}>
                        <Table sx={{ minWidth: 650 }} aria-label="user payments table">
                            <TableHead sx={{ backgroundColor: '#f5f5f5' }}>
                                <TableRow>
                                    <TableCell>ID</TableCell>
                                    <TableCell>Name</TableCell>
                                    <TableCell>Email</TableCell>
                                    <TableCell>Role</TableCell>
                                    <TableCell>Package</TableCell>
                                    <TableCell>Expiry</TableCell>
                                    <TableCell>Permission</TableCell>
                                    <TableCell>Visibility</TableCell>
                                    <TableCell>Chats</TableCell>
                                    <TableCell>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {users?.map((user) => (
                                    <TableRow
                                        key={user.id}
                                        sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                                    >
                                        <TableCell component="th" scope="row">
                                            {user.id}
                                        </TableCell>
                                        <TableCell>{user.name}</TableCell>
                                        <TableCell>{user.email}</TableCell>
                                        <TableCell>
                                            <Chip label={user.role} color={user.role === 'admin' ? 'primary' : 'default'} size="small" />
                                        </TableCell>
                                        <TableCell>{user.package_type || 'N/A'}</TableCell>
                                        <TableCell>{user.expiry_date ? new Date(user.expiry_date).toLocaleDateString() : 'N/A'}</TableCell>
                                        <TableCell>
                                            <FormControlLabel
                                                control={
                                                    <Switch
                                                        checked={!!user.user_permission}
                                                        onChange={() => handleUpdatePayment(user.id, !user.user_permission)}
                                                        color="primary"
                                                    />
                                                }
                                                label={user.user_permission ? "Active" : "Inactive"}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Tooltip title={user.is_hidden ? "User is Hidden" : "User is Visible"}>
                                                <IconButton onClick={() => handleToggleVisibility(user.id, user.is_hidden)}>
                                                    {user.is_hidden ? <VisibilityOffIcon color="error" /> : <VisibilityIcon color="action" />}
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                        <TableCell>
                                            <Tooltip title="View Chats">
                                                <IconButton onClick={() => handleOpenChats(user)}>
                                                    <ChatIcon color="primary" />
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                        <TableCell>
                                            <Button
                                                variant="outlined"
                                                size="small"
                                                onClick={() => handleUpdatePayment(user.id, true)}
                                                sx={{ mr: 1, mb: 1 }}
                                                disabled={!!user.user_permission}
                                            >
                                                Mark Paid
                                            </Button>
                                            <Button
                                                variant="outlined"
                                                color="error"
                                                size="small"
                                                onClick={() => handleUpdatePayment(user.id, false)}
                                                disabled={!user.user_permission}
                                            >
                                                Revoke
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TablePagination
                        rowsPerPageOptions={[5, 10, 25]}
                        component="div"
                        count={pagination.total || 0}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                    />
                </>
            )}

            {/* TAB 2: USER PROFILES */}
            {currentTab === 1 && (
                <>
                    {profilesLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 5 }}><CircularProgress /></Box>
                    ) : (
                        <TableContainer component={Paper}>
                            <Table sx={{ minWidth: 650 }} aria-label="user profiles table">
                                <TableHead sx={{ backgroundColor: '#f5f5f5' }}>
                                    <TableRow>
                                        <TableCell>User ID</TableCell>
                                        <TableCell>Name</TableCell>
                                        <TableCell>Email</TableCell>
                                        <TableCell>Phone</TableCell>
                                        <TableCell>Age</TableCell>
                                        <TableCell>Gender</TableCell>
                                        <TableCell>City</TableCell>
                                        <TableCell>Profession</TableCell>
                                        <TableCell>Religion</TableCell>
                                        <TableCell>Images</TableCell>
                                        <TableCell>Created</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {userProfiles.map((profile) => (
                                        <TableRow key={profile.userId} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                                            <TableCell>{profile.userId}</TableCell>
                                            <TableCell>{profile.firstName} {profile.lastName}</TableCell>
                                            <TableCell>{profile.email}</TableCell>
                                            <TableCell>{profile.phoneNumber}</TableCell>
                                            <TableCell>{profile.age}</TableCell>
                                            <TableCell>{profile.gender}</TableCell>
                                            <TableCell>{profile.city}</TableCell>
                                            <TableCell>{profile.profession}</TableCell>
                                            <TableCell>{profile.religion}</TableCell>
                                            <TableCell>
                                                <Chip label={profile.imageCount || 0} color={profile.imageCount > 0 ? 'success' : 'default'} size="small" />
                                            </TableCell>
                                            <TableCell>{profile.createdAt ? new Date(profile.createdAt).toLocaleDateString() : 'N/A'}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </>
            )}

            {/* TAB 3: AUSPICIOUS REQUESTS */}
            {currentTab === 2 && (
                <>
                    {requestsLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 5 }}><CircularProgress /></Box>
                    ) : (
                        <TableContainer component={Paper}>
                            <Table sx={{ minWidth: 650 }} aria-label="auspicious requests table">
                                <TableHead sx={{ backgroundColor: '#f5f5f5' }}>
                                    <TableRow>
                                        <TableCell>ID</TableCell>
                                        <TableCell>Full Name</TableCell>
                                        <TableCell>Email</TableCell>
                                        <TableCell>Phone</TableCell>
                                        <TableCell>Birthday</TableCell>
                                        <TableCell>Birth Time</TableCell>
                                        <TableCell>Purpose</TableCell>
                                        <TableCell>Ascendant</TableCell>
                                        <TableCell>Submitted</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {auspiciousRequests.map((request) => (
                                        <TableRow key={request.id} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                                            <TableCell>{request.id}</TableCell>
                                            <TableCell>{request.full_name}</TableCell>
                                            <TableCell>{request.email}</TableCell>
                                            <TableCell>{request.phone_number}</TableCell>
                                            <TableCell>{request.birthday ? new Date(request.birthday).toLocaleDateString() : 'N/A'}</TableCell>
                                            <TableCell>{request.birth_time || 'N/A'}</TableCell>
                                            <TableCell>{request.purpose}</TableCell>
                                            <TableCell>{request.ascendant || 'N/A'}</TableCell>
                                            <TableCell>{request.created_at ? new Date(request.created_at).toLocaleString() : 'N/A'}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </>
            )}

            {/* CHAT MODAL */}
            <Dialog
                open={chatModalOpen}
                onClose={handleCloseChatModal}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>
                    {selectedConversation ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <IconButton onClick={() => setSelectedConversation(null)} size="small">
                                <ArrowBackIcon />
                            </IconButton>
                            Chat History
                        </Box>
                    ) : (
                        `Chats for ${selectedUserForChat?.name}`
                    )}
                </DialogTitle>
                <DialogContent dividers>
                    {selectedConversation ? (
                        // MESSAGES VIEW
                        <Box sx={{ height: '400px', overflowY: 'auto' }}>
                            {messagesLoading ? (
                                <CircularProgress />
                            ) : selectedChatMessages.length === 0 ? (
                                <Typography>No messages found.</Typography>
                            ) : (
                                selectedChatMessages.map((msg) => (
                                    <Box
                                        key={msg.id}
                                        sx={{
                                            display: 'flex',
                                            justifyContent: msg.sender_id === selectedUserForChat?.id ? 'flex-start' : 'flex-end',
                                            mb: 1
                                        }}
                                    >
                                        <Paper
                                            sx={{
                                                p: 1.5,
                                                bgcolor: msg.sender_id === selectedUserForChat?.id ? '#f5f5f5' : '#e3f2fd',
                                                maxWidth: '70%'
                                            }}
                                        >
                                            <Typography variant="body1">{msg.message}</Typography>
                                            <Typography variant="caption" color="textSecondary">
                                                {new Date(msg.created_at).toLocaleString()}
                                            </Typography>
                                        </Paper>
                                    </Box>
                                ))
                            )}
                        </Box>
                    ) : (
                        // CONVERSATIONS LIST
                        <List>
                            {chatsLoading ? (
                                <Box sx={{ display: 'flex', justifyContent: 'center' }}><CircularProgress /></Box>
                            ) : selectedUserChats.length === 0 ? (
                                <Typography sx={{ p: 2 }}>No conversations found for this user.</Typography>
                            ) : (
                                selectedUserChats.map((chat) => (
                                    <React.Fragment key={chat.id}>
                                        <ListItem button onClick={() => handleSelectConversation(chat.id)}>
                                            <ListItemText
                                                primary={`Conversation with ${chat.partnerName || 'Unknown User'}`}
                                                secondary={chat.partnerEmail}
                                            />
                                        </ListItem>
                                        <Divider />
                                    </React.Fragment>
                                ))
                            )}
                        </List>
                    )}
                </DialogContent>
            </Dialog>
        </Container>
    );
};

export default AdminDashboard;
